import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { toast } from 'react-toastify';
import { connect, Dispatch } from 'react-redux';
import { IApplicationState, AppActionCreators, OrdersActionCreators, DonorShieldDirectActionCreators, DashActionCreators, ISerologyTestsResponse, IDonorShieldDirectResponse, IAddressValidationResponse } from '@nkr/store/index';
import { Col, Panel, Row, FormGroup, Alert, FormControl, Button, Grid, Checkbox, ControlLabel, HelpBlock } from 'react-bootstrap';
import { IBaseResponse, IListItem, IStreamlinedSerologyOrder } from '@nkr/models/index';
import { IUsPsAddressResponse } from '@nkr/models/usps-address-response';
import { OrganType, StreamlinedSerologyReason } from '@nkr/enums/index';
import { PRE_OP_SEROLOGY_BUSINESS_DAYS_AFTER_REQUEST, addBusinessDays } from '@nkr/utils/blood-draw-validation';
import { IBaseDispatchProps, PHLEBOT_REQUEST_FAILED_MESSAGE } from '../models';
import AddressValidation, { IAddress } from '@nkr/components/address-validation';
import HomeBloodDrawNav from '../nav';
import MaskedInput from 'react-maskedinput';

interface IProps {
    centerId: number,
    zipCode: string,
    donorShieldId?: number,
    inviteToDS?: boolean
}

interface IStateProps {
    siteOrganType: OrganType,
    groupIsSuper: boolean
}

interface IDispatchProps extends IBaseDispatchProps {
    requestStreamlinedSerology: (request: IStreamlinedSerologyOrder, centerId: number) => Promise<IBaseResponse>,
    serologyTests: (centerId: number, organType: number) => Promise<ISerologyTestsResponse>,
    getDonorShieldDirectRecord: (donorShieldId: number) => Promise<IDonorShieldDirectResponse>,
    validateAddress: (address: string, address2: string, city: string, state: string, postalCode: string) => Promise<IAddressValidationResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    data: IStreamlinedSerologyOrder,
    listOfStates: IListItem[],
    serologyList: IListItem[],
    serologyTests: number[],
    disableElements: boolean,
    validation: ISerologyValidation,
    dirty: boolean,
    addressValidation: {
        show: boolean,
        response: IUsPsAddressResponse,
        submittedAddress: IAddress
    }
}

interface ISerologyValidation {
    organTypeIsValid: boolean,
    reasonIsValid: boolean,
    scheduledDonationDateIsValid: boolean,
    bloodDrawDateIsValid: boolean,
    aliasIsValid: boolean,
    recpAliasIsValid: boolean,
    nameIsValid: boolean,
    dobIsValid: boolean,
    dobIsOldEnough: boolean,
    shipToAddress1IsValid: boolean,
    shipToCityIsValid: boolean,
    shipToStateIsValid: boolean,
    shipToZipIsValid: boolean,
    shipToPhoneIsValid: boolean,
    shipToEmailIsValid: boolean
}

class StreamlinedSerology extends React.Component<TProps, IState> {
    readonly state: IState = {
        data: {
            organType: this.props.siteOrganType == OrganType.Liver ? OrganType.Liver : null,
            reason: null,
            scheduledDonationDate: '',
            bloodDrawDate: '',
            alias: '',
            recpAlias: '',
            name: '',
            dob: '',
            serologyTests: '',
            shipToAttn: '',
            shipToAddress1: '',
            shipToAddress2: '',
            shipToCity: '',
            shipToState: '',
            shipToZip: '',
            shipToPhone: '',
            shipToEmail: '',
            isPhlebot: false
        },
        listOfStates: [],
        serologyList: [],
        serologyTests: [],
        disableElements: false,
        validation: {
            organTypeIsValid: true,
            reasonIsValid: true,
            scheduledDonationDateIsValid: true,
            bloodDrawDateIsValid: true,
            aliasIsValid: true,
            recpAliasIsValid: true,
            nameIsValid: true,
            dobIsValid: true,
            dobIsOldEnough: true,
            shipToAddress1IsValid: true,
            shipToCityIsValid: true,
            shipToStateIsValid: true,
            shipToZipIsValid: true,
            shipToPhoneIsValid: true,
            shipToEmailIsValid: true
        },
        dirty: false,
        addressValidation: {
            show: false,
            response: null,
            submittedAddress: null
        }
    };

    componentDidMount = () => {
        this.getData();
    };

    handleTextInputChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;
        let data = _.cloneDeep(this.state.data)

        data[name] = value;

        this.setState({
            data
        });
    }

    handleOrganTypeChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let value = e.currentTarget.value;
        let data = _.cloneDeep(this.state.data);

        data.organType = value === '' ? null : parseInt(value);

        this.setState({
            data
        }, () => {
            if (data.organType != null) {
                this.loadSerologyTests(data.organType);
            }
        });
    }

    handleReasonChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let value = e.currentTarget.value;
        let data = _.cloneDeep(this.state.data);

        data.reason = value === '' ? null : parseInt(value);

        if (data.reason !== StreamlinedSerologyReason.ScheduledDonation) {
            data.scheduledDonationDate = '';
        }

        data.bloodDrawDate = '';

        this.setState({
            data
        });
    }

    handleScheduledDonationDateChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let value = e.currentTarget.value;
        let data = _.cloneDeep(this.state.data);

        data.scheduledDonationDate = value;

        if (data.bloodDrawDate) {
            let isWithinWindow = value != '' && moment(data.bloodDrawDate).isBetween(moment(value).subtract(7, 'days'), this.getBloodDrawDateMax(value), 'day', '[]');

            if (!isWithinWindow) {
                data.bloodDrawDate = '';
            }
        }

        this.setState({
            data
        });
    }

    handleScheduledDonationDateBlur = () => {
        const { scheduledDonationDate } = this.state.data;

        if (scheduledDonationDate && this.props.donorShieldId == null && moment(scheduledDonationDate).isBefore(this.getScheduledDonationDateMin(), 'day')) {
            toast.error(this.getScheduledDonationDateError());

            let data = _.cloneDeep(this.state.data);

            data.scheduledDonationDate = '';
            data.bloodDrawDate = '';

            this.setState({
                data
            });
        }
    }

    handleBloodDrawDateChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let value = e.currentTarget.value;
        let data = _.cloneDeep(this.state.data);

        data.bloodDrawDate = value;

        this.setState({
            data
        });
    }

    handleBloodDrawDateBlur = () => {
        const error = this.state.data.bloodDrawDate ? this.getBloodDrawDateError() : '';

        if (error) {
            toast.error(error);

            let data = _.cloneDeep(this.state.data);

            data.bloodDrawDate = '';

            this.setState({
                data
            });
        }
    }

    handlePhlebot = () => {
        let data = _.cloneDeep(this.state.data);

        data.isPhlebot = !data.isPhlebot;

        this.setState({
            data
        });
    }

    handleTestClick = (val: number) => {
        let serologyTests = _.cloneDeep(this.state.serologyTests);

        if (serologyTests.includes(val)) {
            serologyTests = serologyTests.filter(x => x != val);
        } else {
            serologyTests.push(val);
        }

        this.setState({
            serologyTests
        });
    }

    handleValidate = () => {
        let { organType, reason, scheduledDonationDate, bloodDrawDate, alias, recpAlias, name, dob, shipToAddress1, shipToCity, shipToState, shipToZip, shipToPhone, shipToEmail } = this.state.data;
        let { serologyTests } = this.state;

        let validation: ISerologyValidation = {
            organTypeIsValid: organType != null,
            reasonIsValid: reason != null,
            scheduledDonationDateIsValid: reason !== StreamlinedSerologyReason.ScheduledDonation || (!!scheduledDonationDate && (this.props.donorShieldId != null || moment(scheduledDonationDate).isSameOrAfter(this.getScheduledDonationDateMin(), 'day'))),
            bloodDrawDateIsValid: reason == null || (!!bloodDrawDate && !this.getBloodDrawDateError()),
            aliasIsValid: alias.length > 0,
            recpAliasIsValid: recpAlias.length > 0,
            nameIsValid: name.length > 0,
            dobIsValid: dob != '' || dob != null || dob != undefined,
            dobIsOldEnough: this.isDobOldEnough(dob),
            shipToAddress1IsValid: shipToAddress1.length > 0,
            shipToCityIsValid: shipToCity.length > 0,
            shipToStateIsValid: shipToState.length > 0,
            shipToZipIsValid: shipToZip.length > 0,
            shipToPhoneIsValid: shipToPhone.length > 0,
            shipToEmailIsValid: shipToEmail.length > 0
        };

        if (scheduledDonationDate && !validation.scheduledDonationDateIsValid) {
            toast.error(this.getScheduledDonationDateError());
        }

        let isValid = true

        Object.keys(validation).every(x => {
            if (validation[x] == false) {
                isValid = false;
                return false;
            }

            return true;
        })

        if (serologyTests.length == 0) {
            isValid = false
        }

        this.setState({
            dirty: !isValid,
            validation,
            addressValidation: {
                show: false,
                response: null,
                submittedAddress: null
            }
        }, () => {
            if (isValid) {
                this.handleValidateAddress();
            }

            if (serologyTests.length == 0) {
                this.getData();
            }
        });
    }

    handleValidateAddress = () => {
        let { shipToAddress1, shipToAddress2, shipToCity, shipToState, shipToZip } = this.state.data;

        this.props.showLoading('Validating the shipping address, one moment please...').then(async () => {
            let response = await this.props.validateAddress(shipToAddress1, shipToAddress2, shipToCity, shipToState, shipToZip);

            this.props.hideLoading();

            if (response.data.success) {
                this.handleSubmit();

                return;
            }

            this.setState({
                addressValidation: {
                    show: true,
                    response: response.data.results,
                    submittedAddress: {
                        street: shipToAddress1,
                        street2: shipToAddress2,
                        city: shipToCity,
                        state: shipToState,
                        postalCode: shipToZip
                    }
                }
            });
        });
    }

    handleUseAddress = (recommended: boolean) => {
        let { response } = this.state.addressValidation;

        let data = _.cloneDeep(this.state.data);

        if (recommended) {
            data.shipToAddress1 = response.address1;
            data.shipToAddress2 = response.address2;
            data.shipToCity = response.city;
            data.shipToState = response.state;
            data.shipToZip = response.zip5;
        }

        this.setState({
            data,
            addressValidation: {
                show: false,
                response: null,
                submittedAddress: null
            }
        }, () => {
            this.handleSubmit();
        });
    }

    handleCancelAddress = () => {
        this.setState({
            addressValidation: {
                show: false,
                response: null,
                submittedAddress: null
            }
        });
    }

    handleSubmit = () => {
        let { centerId, hideLoading, showLoading, requestStreamlinedSerology } = this.props;
        let { data, serologyTests } = this.state

        let requestObject: IStreamlinedSerologyOrder = {
            ...data,
            serologyTests: serologyTests.toString(),
            scheduledDonationDate: data.scheduledDonationDate || null,
            bloodDrawDate: data.bloodDrawDate || null
        }

        showLoading('Sending serology request, on moment please').then(() => {
            requestStreamlinedSerology(requestObject, centerId).then((response) => {
                if (response.data.success) {
                    
                    this.setState({
                        disableElements: true
                    });

                    if (response.data.results?.phlebotRequestFailed) {
                        toast.warning(PHLEBOT_REQUEST_FAILED_MESSAGE, {
                            autoClose: 10000,
                            onClose: () => {
                                window.location.href = this.props.inviteToDS
                                    ? '/center/' + this.props.centerId + '/donors/watr/add-request?donorShieldId=' + this.props.donorShieldId
                                    : `/center/${this.props.centerId}/action-center/home-blood-draw`;
                            }
                        });
                    } else if (this.props.inviteToDS) {
                        toast.success('Serology tests succesfully ordered, now routing to Donor Shield to request reimbursement', {
                            autoClose: 2000,
                            onClose: () => {
                                window.location.href = '/center/' + this.props.centerId + '/donors/watr/add-request?donorShieldId=' + this.props.donorShieldId;
                            }

                        })
                    } else {
                        
                        toast.success('Serology tests successfully ordered', {
                            autoClose: 2000,
                            onClose: () => {
                                window.location.href = `/center/${this.props.centerId}/action-center/home-blood-draw`;
                            }
                        });
                    }

                } else {
                    toast.error('Serology tests were unable to be ordered: ' + response.data.message);
                }
                hideLoading();
            }).catch((err: any) => {
                toast.error('Unable to confirm the serology order. Please check Order Tracking before ordering again.')
                hideLoading();
            })
        });
    }

    isDobOldEnough = (dob: string) => {
        if (dob == '' || dob == null) {
            return true;
        }

        var now = moment();

        if (moment(dob).isSameOrBefore(now.add(-18, 'years'))) {
            return true;
        }

        return false;
    }

    getScheduledDonationDateMin = (): moment.Moment => {
        return this.props.groupIsSuper ? moment().startOf('day') : moment().startOf('day').add(10, 'days');
    }

    getScheduledDonationDateError = (): string => {
        return this.props.groupIsSuper ? 'The Scheduled Donation Date cannot be in the past.' : 'The Scheduled Donation Date must be at least 10 days from today.';
    }

    getBloodDrawDateMax = (scheduledDonationDate: string): moment.Moment => {
        return this.props.groupIsSuper ? moment(scheduledDonationDate) : moment(scheduledDonationDate).subtract(2, 'days');
    }

    getPreOpBloodDrawDateMin = (): moment.Moment => {
        return addBusinessDays(moment(), PRE_OP_SEROLOGY_BUSINESS_DAYS_AFTER_REQUEST);
    }

    getBloodDrawDateError = (): string => {
        const { bloodDrawDate, scheduledDonationDate, reason } = this.state.data;

        if ([0, 6].indexOf(moment(bloodDrawDate).day()) >= 0) {
            return 'Weekends cannot be selected for the Scheduled Blood Draw Date.';
        }

        if (moment(bloodDrawDate).isBefore(moment(), 'day')) {
            return 'Past dates cannot be selected for the Scheduled Blood Draw Date.';
        }

        if (reason === StreamlinedSerologyReason.PreOpTesting && moment(bloodDrawDate).isBefore(this.getPreOpBloodDrawDateMin(), 'day')) {
            return `The Scheduled Blood Draw Date must be at least ${PRE_OP_SEROLOGY_BUSINESS_DAYS_AFTER_REQUEST} business days from today (on or after ${this.getPreOpBloodDrawDateMin().format('MM/DD/YYYY')}).`;
        }

        if (reason === StreamlinedSerologyReason.ScheduledDonation && !moment(bloodDrawDate).isBetween(moment(scheduledDonationDate).subtract(7, 'days'), this.getBloodDrawDateMax(scheduledDonationDate), 'day', '[]')) {
            return this.props.groupIsSuper ? 'The Scheduled Blood Draw Date must be within 7 days before the Scheduled Donation Date.' : 'The Scheduled Blood Draw Date must be between 2 and 7 days before the Scheduled Donation Date.';
        }

        return '';
    }

    loadSerologyTests =(organType: OrganType) => {
        let { centerId } = this.props;

        this.props.serologyTests(centerId, organType).then(testData => {
            let serologyList: IListItem[] = [];
            let serologyTests: number[] = [];

            if (testData.data.success) {
                serologyList = testData.data.results.tests
                serologyTests = testData.data.results.centerTests
            } else {
                toast.error(testData.data.message);
            }

            this.setState({
                serologyList,
                serologyTests
            });
        });
    }

    getData = () => {
        let { zipCode, centerId, donorShieldId } = this.props;
        let { data } = this.state;

        if (zipCode == undefined) {
            zipCode = "";
        }

        this.props.showLoading('Getting Test data, one moment please...').then(() => {
            Promise.all([
                this.props.lookUpZipCode(zipCode),
                donorShieldId != null ? this.props.getDonorShieldDirectRecord(donorShieldId) : Promise.resolve(null)
            ]).then(([zipData, donorShieldData]) => {
                let shipToCity = "";
                let shipToState = "";
                let listOfStates: IListItem[] = [];
                let shipToZip = zipCode;

                if (zipData.data.success) {
                    shipToCity = zipData.data.results.city,
                        shipToState = zipData.data.results.state;
                    listOfStates = zipData.data.results.listOfStates
                } else {
                    toast.error(zipData.data.message)
                }

                let donorShieldFields = {};

                if (donorShieldData != null) {
                    if (donorShieldData.data.success) {
                        let dsd = donorShieldData.data.results;
                        let dsdState = listOfStates.find(x => x.id === dsd.donorState)?.abbreviation ?? shipToState;

                        donorShieldFields = {
                            donorShieldDirectId: donorShieldId,
                            organType: this.props.siteOrganType == OrganType.Liver ? OrganType.Liver : (dsd.organType ?? null),
                            reason: StreamlinedSerologyReason.ScheduledDonation,
                            scheduledDonationDate: dsd.surgeryDate ? moment(dsd.surgeryDate, 'MM/DD/YYYY').format('YYYY-MM-DD') : '',
                            alias: dsd.donorAlias ?? '',
                            recpAlias: dsd.recipientAlias ?? '',
                            name: dsd.donorName ?? '',
                            dob: dsd.donorBirthdate ? moment(dsd.donorBirthdate, 'MM/DD/YYYY').format('YYYY-MM-DD') : '',
                            shipToAttn: dsd.donorName ?? '',
                            shipToAddress1: dsd.donorAddressLine1 ?? '',
                            shipToAddress2: dsd.donorAddressLine2 ?? '',
                            shipToCity: dsd.donorCity || shipToCity,
                            shipToState: dsdState,
                            shipToZip: (dsd.donorPostalCode || shipToZip).substring(0, 5),
                            shipToPhone: dsd.donorPhoneNumber ?? '',
                            shipToEmail: dsd.donorEmail ?? ''
                        };
                    } else {
                        toast.error(donorShieldData.data.message);
                    }
                }

                let newData = {
                    ...data,
                    shipToCity,
                    shipToState,
                    shipToZip,
                    ...donorShieldFields
                };

                this.setState({
                    listOfStates,
                    data: newData
                });

                this.loadSerologyTests(newData.organType ?? OrganType.Kidney);

                this.props.hideLoading();
            }).catch((err: any) => {
                toast.error("error loading data");
                this.props.hideLoading()
            })
        });
    };

    render = () => {
        let { serologyList, serologyTests, validation, dirty, listOfStates } = this.state;
        let { organType, reason, scheduledDonationDate } = this.state.data;
        let isValid = Object.keys(validation).filter(x => validation[x] == false).length == 0;
        let isFromDonorShieldDirect = this.props.donorShieldId != null;
        let isLiverSite = this.props.siteOrganType == OrganType.Liver;
        let isScheduledDonation = reason === StreamlinedSerologyReason.ScheduledDonation;
        let isPreOpTesting = reason === StreamlinedSerologyReason.PreOpTesting;
        let reasonNotSelected = reason == null;
        let organTypeDisabled = this.state.disableElements || isFromDonorShieldDirect || isLiverSite;
        let scheduledDonationDateDisabled = this.state.disableElements || isFromDonorShieldDirect || !isScheduledDonation;
        const scheduledDonationDateMin = isFromDonorShieldDirect ? undefined : this.getScheduledDonationDateMin().format('YYYY-MM-DD');
        let bloodDrawDateDisabled = this.state.disableElements || !(isPreOpTesting || (isScheduledDonation && scheduledDonationDate));
        let bloodDrawDateMin = isPreOpTesting ? this.getPreOpBloodDrawDateMin().format('YYYY-MM-DD') : scheduledDonationDate ? moment.max(moment().startOf('day'), moment(scheduledDonationDate).subtract(7, 'days')).format('YYYY-MM-DD') : undefined;
        let bloodDrawDateMax = scheduledDonationDate ? this.getBloodDrawDateMax(scheduledDonationDate).format('YYYY-MM-DD') : undefined;
        let serologyTestsDisabled = this.state.disableElements || reasonNotSelected;

        return (
            <>
                <Grid style={{ paddingTop: 20 }}>
                    {!isValid && this.state.dirty &&
                        <Row>
                            <Col md={12}>
                                <Alert bsStyle='danger' bsSize='sm'>
                                    The fields in red are required...
                                </Alert>
                            </Col>
                        </Row>
                    }

                    <Row>
                        <Col md={12} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <h1 style={{ margin: 0 }}>Order Streamlined Serology Tests</h1>
                            <div><span style={{ color: 'red' }}>*</span> field is required.</div>
                        </Col>
                    </Row>

                    <form>
                        <Row>
                            <Col md={12}>
                                <Panel>
                                    <Panel.Heading style={{ backgroundColor: '#337ab7', color: 'white' }}>
                                        Reason for ordering Streamlined Serology test:
                                    </Panel.Heading>

                                    <Panel.Body>
                                        <Row>
                                            <Col md={3}>
                                                <FormGroup validationState={dirty && validation.organTypeIsValid == false ? 'error' : null}>
                                                    <ControlLabel><span style={{ color: 'red' }}>*</span>&nbsp;Organ Type</ControlLabel>
                                                    <FormControl
                                                        name='organType'
                                                        componentClass={'select'}
                                                        value={organType == null ? '' : organType}
                                                        onChange={this.handleOrganTypeChange}
                                                        disabled={organTypeDisabled}
                                                    >
                                                        <option value={''}>- Please Select -</option>
                                                        <option value={OrganType.Kidney}>Kidney</option>
                                                        <option value={OrganType.Liver}>Liver</option>
                                                    </FormControl>
                                                </FormGroup>
                                            </Col>
                                            <Col md={3}>
                                                <FormGroup validationState={dirty && validation.reasonIsValid == false ? 'error' : null}>
                                                    <ControlLabel><span style={{ color: 'red' }}>*</span>&nbsp;Reason</ControlLabel>
                                                    <FormControl
                                                        name='reason'
                                                        componentClass={'select'}
                                                        value={reason == null ? '' : reason}
                                                        onChange={this.handleReasonChange}
                                                        disabled={this.state.disableElements || isFromDonorShieldDirect}
                                                    >
                                                        <option value={''}>- Please Select -</option>
                                                        <option value={StreamlinedSerologyReason.PreOpTesting}>Pre-op testing</option>
                                                        <option value={StreamlinedSerologyReason.ScheduledDonation}>Scheduled donation</option>
                                                    </FormControl>
                                                </FormGroup>
                                            </Col>
                                            <Col md={3}>
                                                <FormGroup validationState={dirty && validation.scheduledDonationDateIsValid == false ? 'error' : null}>
                                                    <ControlLabel>
                                                        {isScheduledDonation && <span style={{ color: 'red' }}>*</span>}
                                                        &nbsp;Scheduled Donation Date:
                                                    </ControlLabel>
                                                    <FormControl
                                                        name='scheduledDonationDate'
                                                        type='date'
                                                        value={this.state.data.scheduledDonationDate}
                                                        onChange={this.handleScheduledDonationDateChange}
                                                        onBlur={this.handleScheduledDonationDateBlur}
                                                        disabled={scheduledDonationDateDisabled}
                                                        min={scheduledDonationDateMin}
                                                    />
                                                    {!scheduledDonationDateDisabled &&
                                                        <HelpBlock style={{ margin: 0 }}>{this.props.groupIsSuper ? 'Cannot be in the past.' : 'Must be at least 10 days from today.'}</HelpBlock>
                                                    }
                                                </FormGroup>
                                            </Col>
                                            <Col md={3}>
                                                <FormGroup validationState={dirty && validation.bloodDrawDateIsValid == false ? 'error' : null}>
                                                    <ControlLabel>
                                                        {!reasonNotSelected && <span style={{ color: 'red' }}>*</span>}
                                                        &nbsp;Scheduled Blood Draw Date:
                                                    </ControlLabel>
                                                    <FormControl
                                                        name='bloodDrawDate'
                                                        type='date'
                                                        value={this.state.data.bloodDrawDate}
                                                        onChange={this.handleBloodDrawDateChange}
                                                        onBlur={this.handleBloodDrawDateBlur}
                                                        disabled={bloodDrawDateDisabled}
                                                        min={bloodDrawDateMin}
                                                        max={bloodDrawDateMax}
                                                    />
                                                    {!bloodDrawDateDisabled &&
                                                        <HelpBlock style={{ margin: 0 }}>{isPreOpTesting ? 'Must be at least 3 business days from today. Weekends are not allowed.' : this.props.groupIsSuper ? 'Must be within 7 days before the scheduled donation date. Weekends are not allowed.' : 'Must be 2 to 7 days before the scheduled donation date. Weekends are not allowed.'}</HelpBlock>
                                                    }
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                    </Panel.Body>
                                </Panel>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Panel>
                                    <Panel.Heading style={{ backgroundColor: '#337ab7', color: 'white' }}>
                                        Confirm Demographic Information Here
                                    </Panel.Heading>

                                    <Panel.Body>
                                        <FormGroup validationState={dirty && validation.aliasIsValid == false ? 'error' : null}>
                                            <ControlLabel><span style={{ color: 'red' }}>*</span>&nbsp;Donor Alias</ControlLabel>
                                            <FormControl
                                                name='alias'
                                                placeholder={'Alias'}
                                                value={this.state.data.alias}
                                                onChange={this.handleTextInputChange}
                                                disabled={this.state.disableElements}
                                            />
                                        </FormGroup>

                                        <FormGroup validationState={dirty && validation.recpAliasIsValid == false ? 'error' : null}>
                                            <ControlLabel><span style={{ color: 'red' }}>*</span>&nbsp;Recipient Alias</ControlLabel>
                                            <FormControl
                                                name='recpAlias'
                                                placeholder={'RecpAlias'}
                                                value={this.state.data.recpAlias}
                                                onChange={this.handleTextInputChange}
                                                disabled={this.state.disableElements}
                                            />
                                        </FormGroup>

                                        <FormGroup validationState={dirty && validation.nameIsValid == false ? 'error' : null}>
                                            <ControlLabel><span style={{ color: 'red' }}>*</span>&nbsp;Donor Full Name</ControlLabel>
                                            <FormControl
                                                name='name'
                                                type='name'
                                                placeholder={'Name'}
                                                value={this.state.data.name}
                                                onChange={this.handleTextInputChange}
                                                disabled={this.state.disableElements}
                                            />
                                        </FormGroup>

                                        <FormGroup validationState={dirty && (validation.dobIsValid == false || validation.dobIsOldEnough == false) ? 'error' : null}>
                                            <label>
                                                {dirty && validation.dobIsOldEnough == false &&
                                                    <span className='label label-danger'>Age too young.</span>
                                                }
                                                DOB:<span style={{ color: 'red' }}>*</span>
                                            </label>
                                            <FormControl
                                                name='dob'
                                                type='date'
                                                value={this.state.data.dob}
                                                onChange={this.handleTextInputChange}
                                                disabled={this.state.disableElements}
                                            />
                                        </FormGroup>
                                    </Panel.Body>
                                </Panel>
                            </Col>

                            <Col md={6}>
                                <Panel>
                                    <Panel.Heading style={{ backgroundColor: '#337ab7', color: 'white' }}>
                                        Confirm Donor/Patient Shipping Information Here
                                    </Panel.Heading>

                                    <Panel.Body>
                                        <FormGroup>
                                            <Checkbox
                                                checked={this.state.data.isPhlebot}
                                                onClick={this.handlePhlebot}
                                                disabled={this.state.disableElements}
                                            >
                                                (optional) This patient will have their blood drawn by the NKR's national phlebotomy service.
                                            </Checkbox>
                                        </FormGroup>

                                        <FormGroup>
                                            <ControlLabel>Attention:</ControlLabel>
                                            <FormControl
                                                name='shipToAttn'
                                                placeholder={'Attention: Sally Smith'}
                                                value={this.state.data.shipToAttn}
                                                onChange={this.handleTextInputChange}
                                                disabled={this.state.disableElements}
                                            />
                                        </FormGroup>

                                        <FormGroup validationState={dirty && validation.shipToAddress1IsValid == false ? 'error' : null}>
                                            <ControlLabel><span style={{ color: 'red' }}>*</span>&nbsp;Address 1</ControlLabel>
                                            <FormControl
                                                name='shipToAddress1'
                                                placeholder={'1313 MockingBird Lane'}
                                                value={this.state.data.shipToAddress1}
                                                onChange={this.handleTextInputChange}
                                                disabled={this.state.disableElements}
                                            />
                                        </FormGroup>

                                        <FormGroup>
                                            <ControlLabel>Address 2:</ControlLabel>
                                            <FormControl
                                                name='shipToAddress2'
                                                placeholder={'Apt 23'}
                                                value={this.state.data.shipToAddress2}
                                                onChange={this.handleTextInputChange}
                                                disabled={this.state.disableElements}
                                            />
                                        </FormGroup>

                                        <Row>
                                            <Col md={6}>
                                                <FormGroup validationState={dirty && validation.shipToCityIsValid == false ? 'error' : null}>
                                                    <ControlLabel><span style={{ color: 'red' }}>*</span>&nbsp;City</ControlLabel>
                                                    <FormControl
                                                        name='shipToCity'
                                                        placeholder={'Charleston'}
                                                        value={this.state.data.shipToCity}
                                                        onChange={this.handleTextInputChange}
                                                        disabled={this.state.disableElements}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={3}>
                                                <FormGroup validationState={dirty && validation.shipToStateIsValid == false ? 'error' : null}>
                                                    <ControlLabel><span style={{ color: 'red' }}>*</span>&nbsp;State</ControlLabel>
                                                    <FormControl
                                                        name="shipToState"
                                                        componentClass={'select'}
                                                        value={this.state.data.shipToState}
                                                        onChange={this.handleTextInputChange}
                                                        disabled={this.state.disableElements}
                                                    >
                                                        <option value={''}>- Select -</option>
                                                        {listOfStates.map(x =>
                                                            <option value={x.abbreviation}>{x.abbreviation}</option>
                                                        )}
                                                    </FormControl>
                                                </FormGroup>
                                            </Col>
                                            <Col md={3}>
                                                <FormGroup validationState={dirty && validation.shipToZipIsValid == false ? 'error' : null}>
                                                    <ControlLabel><span style={{ color: 'red' }}>*</span>&nbsp;Zip Code</ControlLabel>
                                                    <FormControl
                                                        name='shipToZip'
                                                        placeholder={'29414'}
                                                        maxLength={5}
                                                        value={this.state.data.shipToZip}
                                                        onChange={this.handleTextInputChange}
                                                        disabled={this.state.disableElements}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Col md={6}>
                                                <FormGroup validationState={dirty && validation.shipToPhoneIsValid == false ? 'error' : null}>
                                                    <ControlLabel><span style={{ color: 'red' }}>*</span>&nbsp;Phone #</ControlLabel>
                                                    <MaskedInput
                                                        mask="111-111-1111"
                                                        name="shipToPhone"
                                                        className="form-control"
                                                        placeholder="123-456-7890"
                                                        value={this.state.data.shipToPhone}
                                                        onChange={this.handleTextInputChange}
                                                        disabled={this.state.disableElements}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={6}>
                                                <FormGroup validationState={dirty && validation.shipToEmailIsValid == false ? 'error' : null}>
                                                    <ControlLabel><span style={{ color: 'red' }}>*</span>&nbsp;Email</ControlLabel>
                                                    <FormControl
                                                        name='shipToEmail'
                                                        placeholder={'emailname@domain.com'}
                                                        value={this.state.data.shipToEmail}
                                                        onChange={this.handleTextInputChange}
                                                        disabled={this.state.disableElements}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>

                                    </Panel.Body>
                                </Panel>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <Panel>
                                    <Panel.Heading style={{ backgroundColor: '#337ab7', color: 'white' }}>
                                        Select Serology Tests
                                    </Panel.Heading>
                                    <Panel.Body>
                                        {reasonNotSelected &&
                                            <Alert bsStyle='danger' bsSize='sm'>
                                                Select a Reason above before selecting tests
                                            </Alert>
                                        }

                                        {serologyTests.length == 0 &&
                                            <Alert bsStyle='danger' bsSize='sm'>
                                                Must select at least 1 test
                                            </Alert>
                                        }

                                        {serologyList.map(x =>
                                            <Col md={6}>
                                                <FormGroup style={{ margin: 0 }}>
                                                    <Checkbox
                                                        checked={serologyTests.includes(x.id)}
                                                        onClick={() => this.handleTestClick(x.id)}
                                                        disabled={serologyTestsDisabled || (isScheduledDonation && x.isRequired > 0)}
                                                    >
                                                        {x.description}
                                                        {x.isRequired == 1 && (
                                                            <span style={{ color: '#C00', fontSize: 12, fontStyle: 'italic' }}> * required by OPTN</span>
                                                        )}
                                                        {x.isRequired == 2 && (
                                                            <span style={{ color: '#C00', fontSize: 12, fontStyle: 'italic' }}> * required</span>
                                                        )}
                                                    </Checkbox>
                                                </FormGroup>
                                            </Col>
                                        )}
                                    </Panel.Body>
                                </Panel>
                            </Col>
                        </Row>

                        <Row>
                            <div className='text-center'>
                                <Button disabled={this.state.disableElements || reasonNotSelected} bsStyle='primary' bsSize='lg' onClick={this.handleValidate}>Place Order</Button>
                            </div>
                            <br />
                        </Row>
                    </form>
                </Grid>

                <AddressValidation
                    show={this.state.addressValidation.show}
                    response={this.state.addressValidation.response}
                    submittedAddresss={this.state.addressValidation.submittedAddress}
                    onUseAddress={this.handleUseAddress}
                    onCancel={this.handleCancelAddress}
                />
            </>
        );
    };
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        lookUpZipCode: (code) => {
            return dispatch(OrdersActionCreators.lookUpZipCode(code))
        },
        requestStreamlinedSerology: (request, centerId) => {
            return dispatch(OrdersActionCreators.requestStreamlinedSerology(request, centerId))
        },
        serologyTests: (centerId, organType) => {
            return dispatch(OrdersActionCreators.serologyTests(centerId, organType));
        },
        getDonorShieldDirectRecord: (donorShieldId) => {
            return dispatch(DonorShieldDirectActionCreators.getDonorShieldDirectRecord(donorShieldId));
        },
        validateAddress: (address, address2, city, state, postalCode) => {
            return dispatch(DashActionCreators.validateAddress(address, address2, city, state, postalCode));
        }
    };
};

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        siteOrganType: appState.app.site.organType,
        groupIsSuper: appState.app.user.group.isSuper
    };
};

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(StreamlinedSerology);
